// GA4 Enhanced Ecommerce funnel via GTM's dataLayer.
// Every dataLayer push in the app must go through this module -- no inline
// pushes elsewhere. Gated on a single analytics flag (enableTagManager +
// tagManagerId): tenants without both configured never touch window.dataLayer.
import { AppSettingResponse } from "../@types/DTO/AppSetting";
import { Vehicle, OptionInVehicle } from "../@types/DTO/Vehicles";

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

export interface GA4Item {
  item_id: string;
  item_name: string;
  item_category?: string;
  item_variant?: string;
  price: number;
  quantity: number;
  rental_days?: number;
}

let analyticsEnabled = false;
let gtmInitialized = false;

type GtagEventKey =
  | "search"
  | "view_search_results"
  | "view_item_list"
  | "select_item"
  | "view_item"
  | "add_to_cart"
  | "remove_from_cart"
  | "begin_checkout"
  | "add_payment_info"
  | "add_shipping_info"
  | "purchase";

let eventFlags: Record<GtagEventKey, boolean> = {
  search: false,
  view_search_results: false,
  view_item_list: false,
  select_item: false,
  view_item: false,
  add_to_cart: false,
  remove_from_cart: false,
  begin_checkout: false,
  add_payment_info: false,
  add_shipping_info: false,
  purchase: false,
};

export function isTrackingEnabled(): boolean {
  return analyticsEnabled;
}

// Call once appSettings resolve (App.tsx). Per-tenant: a tenant with
// enableTagManager/tagManagerId off never gets a dataLayer, and every
// trackX() below silently no-ops.
export function initTagManager(
  appSettings: Partial<AppSettingResponse> | null | undefined
): void {
  analyticsEnabled = !!(
    appSettings?.enableTagManager && appSettings?.tagManagerId
  );
  eventFlags = {
    search: appSettings?.enableGtagSearchEvent !== false,
    view_search_results:
      appSettings?.enableGtagViewSearchResultsEvent !== false,
    view_item_list: appSettings?.enableGtagViewItemListEvent !== false,
    select_item: appSettings?.enableGtagSelectItemEvent !== false,
    view_item: appSettings?.enableGtagViewItemEvent !== false,
    add_to_cart: appSettings?.enableGtagAddToCartEvent !== false,
    remove_from_cart: appSettings?.enableGtagRemoveFromCartEvent !== false,
    begin_checkout: appSettings?.enableGtagBeginCheckoutEvent !== false,
    add_payment_info: appSettings?.enableGtagAddPaymentInfoEvent !== false,
    add_shipping_info: appSettings?.enableGtagAddShippingInfoEvent !== false,
    purchase: appSettings?.enableGtagPurchaseEvent !== false,
  };
  if (!analyticsEnabled || gtmInitialized || typeof window === "undefined") {
    return;
  }
  gtmInitialized = true;
  const containerId = appSettings.tagManagerId as string;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
  document.head.appendChild(script);
}

function push(
  payload: Record<string, unknown>,
  eventFlag?: GtagEventKey
): void {
  if (!analyticsEnabled || typeof window === "undefined") return;
  if (eventFlag && !eventFlags[eventFlag]) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

// Nexi's `importo` is in cents; every other source (WireTransfer, Stripe,
// PayPal, currentPriceToPay) is already major units -- normalize once here
// so no push site can accidentally 100x the value.
export function normalizeAmount(amount: number, gateway?: string): number {
  if (amount == null || isNaN(amount)) return 0;
  const isMinorUnits = gateway?.toUpperCase() === "NEXI";
  const value = isMinorUnits ? amount / 100 : amount;
  return Math.round(value * 100) / 100;
}

function toIso(
  value: number | Date | string | null | undefined
): string | undefined {
  if (value == null) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  return isNaN(date.getTime()) ? undefined : date.toISOString();
}

export function rentalDays(
  pickUp: number | Date | string,
  dropOff: number | Date | string
): number {
  const start = pickUp instanceof Date ? pickUp : new Date(pickUp);
  const end = dropOff instanceof Date ? dropOff : new Date(dropOff);
  const diffHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
  return Math.max(1, Math.ceil(diffHours / 24));
}

// ---- item builders --------------------------------------------------

export function buildVehicleItem(params: {
  vehicle: Vehicle;
  itemName: string;
  periodTotal: number;
  days: number;
}): GA4Item {
  const v = params.vehicle?.Vehicle;
  const itemId =
    v?.Code ||
    v?.groupPic?.internationalCode ||
    v?.groupPic?.nationalCode ||
    "";
  return {
    item_id: itemId || "",
    item_name: params.itemName || v?.VehMakeModel?.Name || "",
    item_category: v?.VendorCarMacroGroup || "",
    item_variant: v?.groupPic?.nationalCode || "",
    price: Math.round(params.periodTotal * 100) / 100,
    quantity: 1,
    rental_days: params.days,
  };
}

export function buildExtraItem(params: {
  code: string;
  description: string;
  amount: number;
  quantity: number;
}): GA4Item {
  return {
    item_id: `EXT_${params.code}`,
    item_name: params.description,
    item_category: "extra",
    price: Math.round(params.amount * 100) / 100,
    quantity: params.quantity || 1,
  };
}

export function buildExtraItemsFromOptions(
  options:
    | Record<
        string,
        { details?: string; name?: string; price: number; quantity: number }
      >
    | undefined,
  vehicleOptionals: OptionInVehicle[] | undefined
): GA4Item[] {
  return Object.entries(options || {}).map(([optionName, opt]) => {
    const matchingOptional = vehicleOptionals?.find(
      (o) => o.Equipment?.EquipType === optionName
    );
    return buildExtraItem({
      code: matchingOptional?.Equipment?.Code || optionName,
      description: opt.details || opt.name || optionName,
      amount: opt.price,
      quantity: opt.quantity,
    });
  });
}

export function buildInsuranceItem(params: {
  id: number | string;
  description: string;
  amount: number;
}): GA4Item {
  return {
    item_id: `INS_${params.id}`,
    item_name: params.description,
    item_category: "insurance",
    price: Math.round(params.amount * 100) / 100,
    quantity: 1,
  };
}

// ---- funnel event wrappers -------------------------------------------

export function trackSearch(params: {
  pickUpLocationCode?: string;
  pickUpLocationName?: string;
  dropOffLocationCode?: string;
  dropOffLocationName?: string;
  pickUpDateTime?: number | Date | string;
  dropOffDateTime?: number | Date | string;
  coupon?: string;
}): void {
  push(
    {
      event: "search",
      pickup_location: params.pickUpLocationCode,
      pickup_location_name: params.pickUpLocationName,
      dropoff_location: params.dropOffLocationCode,
      dropoff_location_name: params.dropOffLocationName,
      pickup_datetime: toIso(params.pickUpDateTime),
      dropoff_datetime: toIso(params.dropOffDateTime),
      rental_days:
        params.pickUpDateTime && params.dropOffDateTime
          ? rentalDays(params.pickUpDateTime, params.dropOffDateTime)
          : undefined,
      coupon: params.coupon || undefined,
    },
    "search"
  );
}

export function trackViewSearchResults(params: { resultsCount: number }): void {
  push(
    {
      event: "view_search_results",
      results_count: params.resultsCount,
      no_results: params.resultsCount === 0,
    },
    "view_search_results"
  );
}

// Guard against re-firing on every filter/sort re-render at the call site
// (e.g. a ref keyed on the vehicle-id list) -- this helper only refuses to
// emit an event with zero items, it doesn't dedupe by itself.
export function trackViewItemList(params: {
  currency: string;
  items: GA4Item[];
}): void {
  if (!params.items.length) return;
  push(
    {
      event: "view_item_list",
      ecommerce: { currency: params.currency, items: params.items },
    },
    "view_item_list"
  );
}

export function trackSelectItem(params: {
  currency: string;
  item: GA4Item;
}): void {
  push(
    {
      event: "select_item",
      ecommerce: { currency: params.currency, items: [params.item] },
    },
    "select_item"
  );
}

export function trackViewItem(params: {
  currency: string;
  value: number;
  item: GA4Item;
}): void {
  push(
    {
      event: "view_item",
      ecommerce: {
        currency: params.currency,
        value: params.value,
        items: [params.item],
      },
    },
    "view_item"
  );
}

export function trackAddToCart(params: {
  currency: string;
  value: number;
  items: GA4Item[];
}): void {
  if (!params.items.length) return;
  push(
    {
      event: "add_to_cart",
      ecommerce: {
        currency: params.currency,
        value: params.value,
        items: params.items,
      },
    },
    "add_to_cart"
  );
}

export function trackRemoveFromCart(params: {
  currency: string;
  value: number;
  items: GA4Item[];
}): void {
  if (!params.items.length) return;
  push(
    {
      event: "remove_from_cart",
      ecommerce: {
        currency: params.currency,
        value: params.value,
        items: params.items,
      },
    },
    "remove_from_cart"
  );
}

export function trackBeginCheckout(params: {
  currency: string;
  value: number;
  items: GA4Item[];
  coupon?: string;
}): void {
  push(
    {
      event: "begin_checkout",
      ecommerce: {
        currency: params.currency,
        value: params.value,
        coupon: params.coupon || undefined,
        items: params.items,
      },
    },
    "begin_checkout"
  );
}

export function trackAddPaymentInfo(params: {
  currency: string;
  value: number;
  items: GA4Item[];
  coupon?: string;
  paymentType: string;
}): void {
  push(
    {
      event: "add_payment_info",
      ecommerce: {
        currency: params.currency,
        value: params.value,
        coupon: params.coupon || undefined,
        payment_type: params.paymentType,
        items: params.items,
      },
    },
    "add_payment_info"
  );
}

export function trackAddShippingInfo(params: {
  currency: string;
  value: number;
  items: GA4Item[];
  customerType?: string;
}): void {
  push(
    {
      event: "add_shipping_info",
      ecommerce: {
        currency: params.currency,
        value: params.value,
        items: params.items,
      },
      customer_type: params.customerType || undefined,
    },
    "add_shipping_info"
  );
}

export interface PurchasePayload {
  transactionId: string;
  value: number;
  currency: string;
  items: GA4Item[];
  coupon?: string;
  paymentType?: string;
}

export function trackPurchase(params: PurchasePayload): void {
  if (!params.transactionId) return;
  push(
    {
      event: "purchase",
      ecommerce: {
        transaction_id: params.transactionId,
        value: params.value,
        currency: params.currency,
        coupon: params.coupon || undefined,
        payment_type: params.paymentType,
        items: params.items,
      },
    },
    "purchase"
  );
}

// ---- Google Ads conversion linking (legacy, pre-GA4-migration) ---------
export function trackGoogleAdsConversion(params: {
  enableDataLayer?: boolean;
  event?: string;
  sendTo?: string;
  currency?: string;
  value: number | null;
  transactionId: string | null;
}): void {
  if (!params.enableDataLayer || typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: params.event || "",
    send_to: params.sendTo || "",
    value: params.value,
    currency: params.currency || "",
    transaction_id: params.transactionId,
  });
}

// ---- sessionStorage blob for surviving the gateway redirect -----------
// Extras/insurance/coupon only live in redux (getSelectedVehicleDetails()),
// which several gateways wipe on redirect/reload. The pay page stashes a
// compact snapshot here right before redirecting; the confirmation views
// read it back for the purchase event's items[].

const PURCHASE_ITEMS_KEY = "ga4PurchaseItems";

export interface StoredPurchaseItems {
  items: GA4Item[];
  currency: string;
  value: number;
  coupon?: string;
  paymentType?: string;
}

export function storePurchaseItems(blob: StoredPurchaseItems): void {
  if (!analyticsEnabled || typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(PURCHASE_ITEMS_KEY, JSON.stringify(blob));
  } catch {
    // sessionStorage unavailable (private mode, storage full, etc.) --
    // purchase falls back to whatever redux still has at that point.
  }
}

export function readPurchaseItems(): StoredPurchaseItems | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(PURCHASE_ITEMS_KEY);
    return raw ? (JSON.parse(raw) as StoredPurchaseItems) : null;
  } catch {
    return null;
  }
}

export function clearPurchaseItems(): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(PURCHASE_ITEMS_KEY);
  } catch {
    // ignore
  }
}
